import { Head, Link } from "@inertiajs/react";
import { Badge, Box, Button, Flex, Section, Text, Theme } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { PanelRightClose, X } from "lucide-react";
import { TrackManagerSeason } from "@/types/shared";
import SeasonsTable from "@/shared/tables/track_manager/SeasonsTable";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import SeasonDetailPanel from "@/shared/track_manager/SeasonDetailPanel";
import ExpandableCards from "@/shared/manager/ExpandableCards";
import useIsMobile from "@/shared/hooks/useIsMobile";
import { badgeColors } from "@/shared/constants/constants";

interface Props {
  seasons: TrackManagerSeason[];
}

export default function TrackManagerSeasonsIndex({ seasons }: Props) {
  const isMobile = useIsMobile();
  const [selectedRow, setSelectedRow] = useState<TrackManagerSeason | null>(
    null,
  );

  useEffect(() => {
    if (selectedRow) {
      const updated = seasons.find((s) => s.id === selectedRow.id);
      if (updated) setSelectedRow(updated);
    }
  }, [seasons]);

  if (isMobile) {
    return (
      <div className="flex">
        <Head title="My Seasons" />
        <div className="grow min-w-0">
          <ManagerPageHeader title="Seasons">
            <Button asChild>
              <Link href="/track_manager/seasons/new">New Season</Link>
            </Button>
          </ManagerPageHeader>
          <Section size="1">
            <Box px="3">
              <ExpandableCards
                items={seasons}
                getKey={(season) => season.id}
                emptyMessage="You don't have any seasons yet."
                renderSummary={(season) => (
                  <Flex direction="column" gap="1">
                    <p className="truncate text-[15px] font-bold leading-tight">
                      {season.name}
                    </p>
                    <span className="truncate text-sm text-gray11">
                      {season.track_name} · {season.event_count}{" "}
                      {season.event_count === 1 ? "event" : "events"}
                    </span>
                    <Flex gap="2" mt="1" wrap="wrap">
                      <Badge
                        variant="soft"
                        color={badgeColors[season.status]}
                        style={{ textTransform: "capitalize" }}
                      >
                        {season.status}
                      </Badge>
                      {season.phase && (
                        <Badge
                          variant="soft"
                          color={badgeColors[season.phase]}
                          style={{ textTransform: "capitalize" }}
                        >
                          {season.phase}
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                renderDetail={(season) => <SeasonDetailPanel season={season} compact />}
              />
            </Box>
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Head title="My Seasons" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Seasons">
          <Button asChild>
            <Link href="/track_manager/seasons/new">New Season</Link>
          </Button>
        </ManagerPageHeader>
        <Dialog.Root
          open={selectedRow !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedRow(null);
          }}
        >
          <Section size="1">
            <Box style={{ overflowX: "auto" }}>
              <SeasonsTable seasons={seasons} setSelectedRow={setSelectedRow} />
            </Box>
          </Section>

          <Dialog.Portal>
            <Theme>
              <Dialog.Overlay
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "var(--color-overlay)",
                }}
              />
              <Dialog.Content
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: "min(440px, 100vw)",
                  height: "100vh",
                  background: "var(--color-panel-solid)",
                  padding: "var(--space-5)",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <Flex justify="between" align="center">
                  <Dialog.Title asChild>
                    <Text size="5" weight="bold">
                      Season Detail
                    </Text>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="1">
                      <X size="16px" />
                    </Button>
                  </Dialog.Close>
                </Flex>
                {selectedRow && <SeasonDetailPanel season={selectedRow} />}
                <Dialog.Close asChild>
                  <Button
                    variant="soft"
                    size="3"
                    className="sm:hidden"
                    style={{
                      position: "sticky",
                      bottom: 0,
                      alignSelf: "flex-end",
                      marginTop: "auto",
                      backgroundColor: "var(--color-panel-solid)",
                    }}
                  >
                    <PanelRightClose size="16px" />
                    Close
                  </Button>
                </Dialog.Close>
              </Dialog.Content>
            </Theme>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
}
